import { UniqueIdentifier } from "@dnd-kit/core";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import PickupTruckIcon from "@redotech/redo-icons/truck-01.svg";
import PickupTruckFilledIcon from "@redotech/redo-icons/truck-01_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  OutboundViewCountsContext,
  OutboundViewsContext,
} from "@redotech/redo-merchant-fulfillment-app/outbound-views";
import { OrderManagementPermissions } from "@redotech/redo-model/permissions";
import { TeamType } from "@redotech/redo-model/team";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext, useMemo } from "react";
import { useMatch } from "react-router-dom";
import {
  ORDERS_ICON,
  ORDERS_ICON_FILLED,
  OrderManagementSuiteStatusesContext,
} from "../../../../redo-suite/order-management-cloud";
import { PickupsItem } from "./pickups";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "./view-to-tree-utils";

export const OrdersItem = memo(function OrdersItem() {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const { omsEnabled } = useContext(OrderManagementSuiteStatusesContext);
  const urlPrefix = routes.ordersOutboundLabelsHref;
  const anyChildSelected = useMatch(`${urlPrefix}/*`);

  const outboundViewsContext = useRequiredContext(OutboundViewsContext);
  const outboundViewCountsContext = useContext(OutboundViewCountsContext);

  const canViewAllOrdersTab = useHasPermission(
    OrderManagementPermissions.VIEW_ORDER,
  );
  const canViewShippingTabs = useHasPermission(
    OrderManagementPermissions.MANAGE_SHIPMENTS,
  );
  const canViewScan = useHasPermission(
    OrderManagementPermissions.SCAN_AND_VERIFY,
  );

  const canViewRewards =
    team.settings.outboundLabels?.pointsSettings?.enabled &&
    canViewShippingTabs;

  const allViews = useMemo(() => {
    return [...outboundViewsContext.views];
  }, [outboundViewsContext]);

  const allFolders = useMemo(() => {
    return [...outboundViewsContext.folders];
  }, [outboundViewsContext]);

  const viewsWithoutFolders = useMemo(() => {
    return allViews.filter((view) => !view.folderId);
  }, [allViews]);

  const foldersWithViews = useMemo<
    FolderWithView<"fulfillment-groups">[]
  >(() => {
    return allFolders.map((folder) => {
      return {
        folder,
        views: allViews.filter((view) => view.folderId === folder._id),
      };
    });
  }, [allFolders, allViews]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: outboundViewCountsContext?.viewCounts,
      folderCounts: outboundViewCountsContext?.folderCounts,
      hideZeroNotifications: true,
    });
  }, [
    viewsWithoutFolders,
    foldersWithViews,
    urlPrefix,
    outboundViewCountsContext?.viewCounts,
    outboundViewCountsContext?.folderCounts,
  ]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    outboundViewsContext.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);

    const { views, folders } = treeToViewsAndFolders<"fulfillment-groups">(
      flattenedItems,
      allViews,
      allFolders,
    );

    outboundViewsContext.updateOrdering(views, folders);
  });

  const showAllOrders =
    team.teamType !== TeamType.FulfillmentService && canViewAllOrdersTab;
  const hideBulkFulfillment =
    team?.settings?.outboundLabels?.batchSuggestions?.enabled === false;

  if (!canViewAllOrdersTab && !canViewShippingTabs && !canViewScan) {
    return null;
  }

  if (!omsEnabled) {
    return (
      <>
        {showAllOrders && (
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: routes.getOrdersOutboundLabelsSubRoute("orders"),
            }}
            FilledIcon={ORDERS_ICON_FILLED}
            friendlyName="All orders"
            id="all-orders"
            UnfilledIcon={ORDERS_ICON}
          />
        )}
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getOrdersOutboundLabelsSubRoute("about"),
          }}
          FilledIcon={PickupTruckFilledIcon}
          friendlyName="Shipments"
          id="about-oms"
          isNew
          UnfilledIcon={PickupTruckIcon}
        />
      </>
    );
  }

  const subroute = canViewShippingTabs
    ? routes.getOrdersOutboundLabelsSubRoute("awaiting-shipment")
    : canViewAllOrdersTab
      ? routes.getOrdersOutboundLabelsSubRoute("orders")
      : canViewScan
        ? routes.getOrdersOutboundLabelsSubRoute("scan")
        : routes.getOrdersOutboundLabelsSubRoute("dashboard");

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: subroute }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={ORDERS_ICON_FILLED}
      friendlyName="Orders"
      id="orders"
      UnfilledIcon={ORDERS_ICON}
    >
      {canViewShippingTabs && (
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getOrdersOutboundLabelsSubRoute("dashboard"),
          }}
          friendlyName="Dashboard"
          id="orders-outbound-labels-dashboard"
        />
      )}
      {canViewRewards && (
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getOrdersOutboundLabelsSubRoute("rewards"),
          }}
          friendlyName="Rewards"
          id="orders-rewards"
        />
      )}
      {!hideBulkFulfillment && canViewShippingTabs && (
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getOrdersOutboundLabelsSubRoute("bulk-fulfillments"),
          }}
          friendlyName="Bulk shipments"
          id="orders-bulk-fulfillments"
        />
      )}
      {showAllOrders && (
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getOrdersOutboundLabelsSubRoute("orders"),
          }}
          friendlyName="All orders"
          id="all-orders"
        />
      )}
      {canViewShippingTabs && (
        <>
          <SortableTree
            items={sortableItems}
            onRequestEdit={handleRequestEdit}
            setItems={handleUpdateOrdering}
          />
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: routes.getOrdersOutboundLabelsSubRoute("create"),
            }}
            friendlyName="+ Add new view"
            id="orders-outbound-labels-create"
          />
          <RedoSecondaryNavItem
            action={{
              type: "button",
              onClick: () => {
                outboundViewsContext.openCreateFolderModal();
              },
            }}
            friendlyName="+ Add new folder"
            id="orders-outbound-labels-create-folder"
          />
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: routes.getOrdersOutboundLabelsSubRoute("batch-history"),
            }}
            friendlyName="Batch history"
            id="orders-batch-history"
          />
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: routes.getOrdersOutboundLabelsSubRoute("eod-manifest"),
            }}
            friendlyName="EOD Manifest"
            id="orders-eod-manifest"
          />
        </>
      )}
      {canViewScan && (
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getOrdersOutboundLabelsSubRoute("scan"),
          }}
          friendlyName="Scan"
          id="scan"
        />
      )}

      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getOrdersOutboundLabelsSubRoute("print-jobs"),
        }}
        friendlyName="Print jobs"
        id="print-jobs"
      />

      <PickupsItem />
    </RedoSecondaryNavItem>
  );
});
